import { useEffect, useState } from "react";
import { annonceDuServeur, contexteAnnonce } from "./annonce.js";
import { loadAccount, type LocalAccount } from "./adapters/account.js";
import { Collectif } from "./screens/collectif.js";
import { Regie } from "./screens/regie.js";
import { Projection } from "./regie/Projection.js";
import { Soiree } from "./hoa/Soiree.js";
import { ESPACES_HUMAINS, entreeParQr } from "./espaces.js";
import { poserEvenement } from "./hoa/voix.js";

// Le QR code d'un événement (`/e/<id>`) : l'événement devient celui de
// l'appareil ; la soirée s'ouvre sur ses tables.
const parQr = entreeParQr();
if (ESPACES_HUMAINS) document.title = "Espaces Humains";
if (parQr) poserEvenement({ id: parQr, nom: "", voix: null });

type Route = { name: "accueil" | "compte" | "collectif" | "regie" | "projection" | "session"; sessionId?: string };

function readRoute(): Route {
  // La régie vit sur un chemin à part, `/regie`, qu'aucun écran ne montre.
  if (location.pathname.replace(/\/+$/, "") === "/regie") return { name: "regie" };
  if (location.pathname.replace(/\/+$/, "") === "/regie/projection") return { name: "projection" };
  const h = location.hash.replace(/^#/, "");
  if (h.startsWith("/collectif")) return { name: "collectif", sessionId: new URLSearchParams(h.split("?")[1] ?? "").get("session") ?? undefined };
  if (h.startsWith("/compte") || h.startsWith("/profil")) return { name: "compte" };
  if (h.startsWith("/s/")) return { name: "session", sessionId: decodeURIComponent(h.slice(3)) };
  return { name: "accueil" };
}

export function App(): JSX.Element {
  const [route, setRoute] = useState<Route>(readRoute);
  useEffect(() => {
    const on = () => setRoute(readRoute());
    addEventListener("hashchange", on);
    return () => removeEventListener("hashchange", on);
  }, []);

  const [account, setAccount] = useState<LocalAccount | null>(null);
  // La soirée attend le compte de l'appareil avant de démarrer : sinon une
  // tribu en cours ou un lien de salle était rejoint sans clé, en anonyme —
  // ni points, ni sanction possible.
  const [comptePret, setComptePret] = useState(false);
  useEffect(() => {
    void loadAccount()
      .then(setAccount, () => setAccount(null))
      .finally(() => setComptePret(true));
  }, []);

  /* L'annonce de la régie, partout.
   *
   * Elle n'arrivait que par le fil d'une session : seuls les téléphones déjà
   * entrés dans une salle la voyaient. Or « le 2e ferme à 23 h » s'adresse
   * d'abord à ceux qui n'ont pas encore choisi — et ceux-là étaient
   * précisément les seuls à ne pas la recevoir. On la lit donc ici, à la
   * racine, quel que soit l'écran affiché : accueil, ville, compte, salle.
   *
   * Vingt secondes : une annonce de soirée n'est pas une notification, elle
   * peut arriver avec un peu de retard sans rien perdre. */
  useEffect(() => {
    let vivant = true;
    const lire = async (): Promise<void> => {
      try {
        const r = await fetch(`/api/annonce?${contexteAnnonce()}`);
        const j = (await r.json()) as { annonce?: { texte: string; a: number } | null };
        if (vivant && j.annonce) annonceDuServeur(j.annonce.a, j.annonce.texte);
      } catch {
        /* hors ligne : l'annonce n'est pas ce qu'on va aller réparer */
      }
    };
    void lire();
    const id = window.setInterval(() => void lire(), 20_000);
    return () => {
      vivant = false;
      window.clearInterval(id);
    };
  }, []);

  if (route.name === "collectif") return <Collectif sessionId={route.sessionId ?? ""} />;
  if (route.name === "regie") return <Regie />;
  if (route.name === "projection") return <Projection />;
  if (!comptePret) return <></>;
  // L'ancienne page « #/compte » (douze mots) : le compte vit maintenant
  // dans Paramètres › Mon compte.
  return (
    <Soiree
      account={account}
      sessionId={route.sessionId}
      ouvrir={route.name === "compte" ? "parametres" : undefined}
      onAccount={setAccount}
      onReceipt={() => void loadAccount().then(setAccount)}
    />
  );
}

